import { ApiError } from './fetch';
import { DeviceId, StreamIndex, UtcTime } from 'types';
import JMuxer = require('jmuxer');
import { baseUrl } from './baseUrl';

function trace(message: string): void {
    console.log(message);
}

function error(message: string): void {
    console.error(message);
}

const MaxRetry = 5;
const RetryDelay = 3000; // reconmect retry delay in ms
const FlushingTime = 1000; // in ms
const MaxPreambleSize = 8;

export class WebsocketStreamer {
    videoElement: HTMLVideoElement | null;
    ws: WebSocket | null;
    jmuxer: JMuxer | null;
    deviceId: DeviceId | null;
    streamIndex: StreamIndex | null;
    utcStreamBaseTime: UtcTime | null; // When did the current stream start playing (relative to the stream)
    utcClientBaseTime: UtcTime | null; // When did the current stream start playing (relative to the client)
    skipToKeyFrame: boolean;
    retryCount: number;
    isAudioEnabled: boolean;
    isWsOpen: boolean;
    isReceivingFrames: boolean;
    isExpectedClose: boolean;

    constructor() {
        this.ws = null;
        this.videoElement = null;
        this.utcStreamBaseTime = null;
        this.utcClientBaseTime = null;
        this.isReceivingFrames = false;
        this.skipToKeyFrame = false;
        this.jmuxer = null;
        this.isExpectedClose = false;
        this.retryCount = 0;
        this.isWsOpen = false;
        this.isAudioEnabled = false;
    }

    //
    // Private methods for Websocket
    //
    private OnOpen() {
        trace('Socket Opened');
        this.isWsOpen = true;
        this.retryCount = 0; // Reset the retry count to 0 on successful opening
    }

    private async OnClose() {
        this.isWsOpen = false;

        // if this was not expected to close, re-open the connection
        if (!this.isExpectedClose) {
            error('Unexpected socket closure');
            this.isExpectedClose = false;
            while (this.retryCount < MaxRetry && !this.isWsOpen) {
                trace(`reconnect attempt: ${this.retryCount}`);
                const delayTime = RetryDelay * (this.retryCount * this.retryCount); // exponential delay based on retries
                await new Promise(r => setTimeout(r, delayTime));
                this.retryCount++;
                this.openWebSocketConnection();
                await new Promise(r => setTimeout(r, RetryDelay));
            }
            if (!this.isWsOpen) {
                error('Unable to Re-Connect to Socket Host');
            }
        }
        else {
            trace('Closed');
        }
    }

    private OnMessage(event: any) {
        // check if the browser is hidden. Skip to keyframe when it is no longer hidden
        if (document.hidden) {
            this.skipToKeyFrame = true;
            return;
        }
        // parse the message data
        const parsedData = this.parse(event.data);
        if (!document.hidden && this.skipToKeyFrame) {
            // if the current frame is a keyframe, reset flag
            if (parsedData.frameType === 0) {
                this.skipToKeyFrame = false;
            }
        }

        if (this.jmuxer && !this.skipToKeyFrame) {
            this.jmuxer.feed(parsedData);
        }
        else {
            trace('not appending message to feed');
        }
    }

    private OnError(event: any) {
        error(`WS error ${event}`);
        throw new Error(`WS error ${event}`);
    }

    private parse(data: any) {
        const input = new Uint8Array(data);
        const dv = new DataView(input.buffer);
        const fps = dv.getUint16(0, true);
        const frameType = dv.getUint16(2, true);
        const audioLength = dv.getUint16(4, true);
        const audio = input.subarray(6, (audioLength + 6));
        const video = input.subarray(audioLength + 6);
        
        if (audioLength === 0) {
            return {
                video: video,
                duration: 1000 / fps,
                frameType: frameType,
            };
        }

        return {
            frameType: frameType,
            audio: audio,
            video: video,
            duration: 1000 / fps,
        };
    }

    private async openWebSocketConnection(utcTime: UtcTime = 0): Promise<void> {
        
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(`wss://${location.host}${baseUrl}/ws/${this.deviceId}/${this.streamIndex}/${utcTime}`);
            ws.binaryType = 'arraybuffer';

            const handlePreamble = (event: any) => {
                // parse the message data
                const messageData = new Uint8Array(event.data);
    
                // preamble message states whether RTSP Connection could open. set the isReceivingFrames to true
                if (messageData.length < MaxPreambleSize) {
                    const md = new DataView(messageData.buffer);
                    const status = md.getUint16(0, true);
                    if (status === 200) {
                        this.isReceivingFrames = true;
                        this.utcClientBaseTime = Date.now();
                        trace('RTSP Connection Established');
                        ws.removeEventListener('message', handlePreamble);
                        ws.onopen = () => this.OnOpen();
                        ws.onclose = () => this.OnClose();
                        ws.onerror = event => this.OnError(event);
                        ws.onmessage = event => this.OnMessage(event);
                        this.ws = ws;
                        this.retryCount = 0;
                        this.isWsOpen = true;
                    }
                    else {
                        this.isReceivingFrames = false;
                        error('RTSP Connection Error');
                    }
                    ws.removeEventListener('message', handlePreamble);
                    resolve();
                }
            };
    
            ws.addEventListener('message', handlePreamble);
        });
    }

    //
    //  Public Methods
    //
    public async connect(
        videoElement: HTMLVideoElement | null,
        deviceId: DeviceId,
        streamIndex: StreamIndex,
        audioEnabled: boolean,
        utcTime: UtcTime = 0): Promise<UtcTime> {
        trace('attempting to connect socket');
        if (!videoElement) {
            error('video element is null');
            throw new ApiError('341'); // Operation failed
        }

        try {
            this.deviceId = deviceId;
            this.streamIndex = streamIndex;
            this.videoElement = videoElement;
            
            if (audioEnabled) {
                this.jmuxer = new JMuxer({
                    node: videoElement,
                    mode: 'both',
                    flushingTime: FlushingTime,
                    debug: false,
                });
            }
            else {
                this.jmuxer = new JMuxer({
                    node: videoElement,
                    mode: 'video',
                    flushingTime: FlushingTime,
                    debug: false,
                });
            }

            await this.openWebSocketConnection(utcTime);
            if (!this.isReceivingFrames) {
                error('preamble failed');
                await this.disconnect();
                throw new ApiError('341');
            }

            utcTime = utcTime || Date.now();
            this.utcStreamBaseTime = utcTime;
            trace(`device Id: ${deviceId} | streamIndex: ${streamIndex} | utcTime: ${this.utcStreamBaseTime}`);
            return utcTime;
        }
        catch (err) {
            await this.disconnect();
            error(`Failed to connect to socket: ${err.message}`);
            throw new ApiError('6.9622.3792'); // Failed to connect
        }
    }

    public disconnect(): UtcTime {
        this.isExpectedClose = true;
        if (this.ws) {
            trace('disconnect');
            try {
                this.ws.close(1000, 'Closing from client');
            }
            catch (err) {
                error(`Failure closing socket connection: ${err.message}`);
            }
            this.isWsOpen = false;
            this.ws.onopen = null;
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws = null;
        }

        this.isReceivingFrames = false;

        if (this.jmuxer) {
            this.jmuxer.destroy();
            this.jmuxer = null;
        }

        if (this.videoElement) {
            this.videoElement.srcObject = null;
            this.videoElement = null;
        }

        // Calculate what we think the current video time is
        // PTODO: would like to get exact time stamp (perhaps open a data channel at the beginning to receive info like timestamp and decorations?)
        const delta = this.utcClientBaseTime ? Date.now() - this.utcClientBaseTime : 0;
        const utcStopTime = (this.utcStreamBaseTime || Date.now()) + delta;

        this.streamIndex = null;
        this.deviceId = null;
        this.utcClientBaseTime = null;
        this.utcStreamBaseTime = null;
        this.retryCount = 0;

        return utcStopTime;
    }

    public isConnected() {
        return this.isWsOpen;
    }

    public isStreaming(): boolean {
       return this.isReceivingFrames;
    }
}



// WEBPACK FOOTER //
// ./src/api/websocketStreamer.ts